import { useMemo, useRef, useEffect, useState } from "react"
import { LEAGUES_SORT_PRIORITY } from "@/Overtime/constants/sports"
import { type League } from "overtime-utils"
import { StatusFilter } from "@/Overtime/enums/markets"
import { type SupportedNetwork } from "@/Overtime/types/network"
import useSportsMarketsV2Query from "@/Overtime/queries/useSportsMarketsV2Query"

type Market = {
  leagueId: League
  [key: string]: any
}

type useOvertimeHotEventsProps = {
  networkId: SupportedNetwork
  maxGames?: number
  isLive?: boolean
}

export const useOvertimeHotEvents = ({
  networkId,
  maxGames = 9,
}: useOvertimeHotEventsProps) => {
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  const hasLoadedOnce = useRef(false)
  const previousData = useRef<any>(null)

  const openSportMarketsQuery = useSportsMarketsV2Query({
    statusFilter: StatusFilter.OPEN_MARKETS,
    includeProofs: false,
    networkConfig: { networkId },
    ticket: undefined,
    isHotEventsQuery: true,
  })

  const openSportMarkets = useMemo(() => {
    if (openSportMarketsQuery.isSuccess && openSportMarketsQuery.data) {
      return openSportMarketsQuery.data[StatusFilter.OPEN_MARKETS]
    }
    return undefined
  }, [openSportMarketsQuery.data, openSportMarketsQuery.isSuccess])

  // Track initial load and data changes
  useEffect(() => {
    if (openSportMarketsQuery.isSuccess && !hasLoadedOnce.current) {
      hasLoadedOnce.current = true
      setIsInitialLoad(false)
      previousData.current = openSportMarkets
    } else if (openSportMarketsQuery.isSuccess && hasLoadedOnce.current) {
      // Check if data has actually changed
      const currentData = JSON.stringify(openSportMarkets)
      const prevData = JSON.stringify(previousData.current)

      if (currentData !== prevData) {
        previousData.current = openSportMarkets
        setIsInitialLoad(false)
      }
    }
  }, [openSportMarketsQuery.isSuccess, openSportMarkets])

  const sortedMarkets = useMemo(() => {
    if (!openSportMarkets) {
      return []
    }

    // Validate input data
    if (!Array.isArray(openSportMarkets)) {
      console.warn("useLeagueSortedMarkets: openSportMarkets is not an array")
      return []
    }

    // Helper function to validate if a market has valid child markets
    const hasValidChildMarkets = (market: Market): boolean => {
      if (!market.childMarkets || !Array.isArray(market.childMarkets)) {
        return false
      }

      // Check if at least one child market is open and has valid odds
      return market.childMarkets.some(childMarket => {
        // Check if child market is open
        if (
          !childMarket.isOpen ||
          childMarket.isPaused ||
          childMarket.isCancelled
        ) {
          return false
        }

        // Check if child market has valid odds (at least one non-zero odd)
        if (!childMarket.odds || !Array.isArray(childMarket.odds)) {
          return false
        }

        return childMarket.odds.some(odd => odd > 0)
      })
    }

    // Helper function to validate if a market itself is valid
    const isValidMarket = (market: Market): boolean => {
      // Basic validation
      if (!market || typeof market !== "object") {
        return false
      }

      // Check if market is open and not paused/cancelled
      if (!market.isOpen || market.isPaused || market.isCancelled) {
        return false
      }

      // Check if market has valid odds
      if (!market.odds || !Array.isArray(market.odds)) {
        return false
      }

      return market.odds.some(odd => odd > 0)
    }

    // Group markets by league and pre-filter invalid markets
    const marketsByLeague = openSportMarkets.reduce(
      (acc, market) => {
        // Skip markets that are not valid themselves
        if (!isValidMarket(market)) {
          return acc
        }

        if (!acc[market.leagueId]) {
          acc[market.leagueId] = []
        }
        acc[market.leagueId].push(market)
        return acc
      },
      {} as Record<League, Market[]>
    )

    // Sort leagues by priority
    const sortedLeagues = LEAGUES_SORT_PRIORITY.filter(
      league => marketsByLeague[league] && marketsByLeague[league].length > 0
    )

    const result: Market[] = []
    let gameCount = 0
    let round = 0 // 0 for first game from each league, 1 for second game, etc.
    const maxRounds = 20 // Increased from 10 to handle more edge cases

    while (gameCount < maxGames && round < maxRounds) {
      // Prevent infinite loop
      for (const league of sortedLeagues) {
        if (gameCount >= maxGames) {
          break
        }

        const leagueMarkets = marketsByLeague[league]
        if (leagueMarkets && leagueMarkets[round]) {
          const market = leagueMarkets[round]

          // Check if market has valid child markets
          if (!hasValidChildMarkets(market)) {
            // Skip this market and try the next one in the same league
            let nextMarketIndex = round + 1
            let foundValidMarket = false

            while (
              nextMarketIndex < leagueMarkets.length &&
              gameCount < maxGames &&
              !foundValidMarket
            ) {
              const nextMarket = leagueMarkets[nextMarketIndex]

              // Check if next market has valid child markets
              if (hasValidChildMarkets(nextMarket)) {
                result.push(nextMarket)
                gameCount++
                foundValidMarket = true
              }
              nextMarketIndex++
            }
          } else {
            result.push(market)
            gameCount++
          }
        }
      }
      round++
    }

    return result
  }, [openSportMarkets, maxGames])

  const result = {
    sortedMarkets,
    isLoading: isInitialLoad && openSportMarketsQuery.isFetching,
    isError: openSportMarketsQuery.isError,
    error: openSportMarketsQuery.error,
  }

  console.groupEnd()
  return result
}
